// src/modules/gyms/components/BranchFormModal.tsx

import {
  Modal,
  Dropdown,
  Form,
  Button,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { FormEvent, useState, useEffect, useRef, useContext } from "react";
import {
  GymBranch,
  AMENITIES_LIST,
  Amenity,
  BranchImage,
  CategoryImage,
} from "../models/gym.model";
import { message, Switch } from "antd";
import { gymPartnerService } from "../services/gym.service";
import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import { hasManagePermission } from "@modules/auth/utils/role.utils";
import { GoogleMapsLinkModal } from "./GoogleMapsLinkModal";

// Danh sách danh mục có thể chọn
const CATEGORY_OPTIONS = [
  { value: "gym", label: "Gym" },
  { value: "yoga", label: "Yoga" },
  { value: "crossfit", label: "CrossFit" },
  { value: "boxing", label: "Boxing" },
  { value: "pilates", label: "Pilates" },
  { value: "cardio", label: "Cardio" },
  { value: "weightlifting", label: "Tạ" },
  { value: "functional", label: "Tập luyện chức năng" },
  { value: "recovery", label: "Phục hồi" },
];

interface BranchFormModalProps {
  show: boolean;
  brandId: string;
  brandName?: string;
  editingBranch?: GymBranch | null;
  onSave: (
    data: Omit<GymBranch, "id" | "createdAt" | "updatedAt">,
    openingHoursOverride?: Record<
      string,
      { start_time: string; end_time: string }
    >,
  ) => Promise<void>;
  onClose: () => void;
  brandOptions?: { id: string; name: string }[];
  onBrandChange?: (id: string) => void;
}

const emptyForm = (
  brandId: string,
): Omit<GymBranch, "id" | "createdAt" | "updatedAt"> => ({
  brandId,
  name: "",
  address: "",
  lat: null,
  lng: null,
  operatingHoursFrom: "06:00",
  operatingHoursTo: "22:00",
  amenities: [],
  description: "",
  ctaLink: "",
  images: [],
  openingHours: undefined,
  isActive: true,
  categories: [],
});

export const BranchFormModal = ({
  show,
  brandId,
  editingBranch,
  onSave,
  onClose,
  brandOptions,
  onBrandChange,
}: BranchFormModalProps) => {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const authArgs = { auth, setAuth, logout };
  const canManage = hasManagePermission(auth);

  const [form, setForm] = useState(emptyForm(brandId));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [existingImages, setExistingImages] = useState<BranchImage[]>([]);
  const [newImageFiles, setNewImageFiles] = useState<
    { file: File; category?: string; preview: string }[]
  >([]);

  const [uploading, setUploading] = useState(false);
  const [showGoogleMapsModal, setShowGoogleMapsModal] = useState(false);

  const [selectedBrandId, setSelectedBrandId] = useState(brandId || "");

  const [openingHoursMode, setOpeningHoursMode] = useState<"same" | "perDay">(
    "same",
  );
  const [openingHoursPerDay, setOpeningHoursPerDay] = useState<
    Record<string, { start: string; end: string; isClosed: boolean }>
  >({
    monday: { start: "06:00", end: "22:00", isClosed: false },
    tuesday: { start: "06:00", end: "22:00", isClosed: false },
    wednesday: { start: "06:00", end: "22:00", isClosed: false },
    thursday: { start: "06:00", end: "22:00", isClosed: false },
    friday: { start: "06:00", end: "22:00", isClosed: false },
    saturday: { start: "06:00", end: "22:00", isClosed: false },
    sunday: { start: "06:00", end: "22:00", isClosed: false },
  });

  const [otherAmenities, setOtherAmenities] = useState<string[]>([]);
  const [otherAmenityInput, setOtherAmenityInput] = useState("");

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);

  const [amenitiesDropdownOpen, setAmenitiesDropdownOpen] = useState(false);

  const MAX_IMAGE_SIZE_MB = 2;
  const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

  useEffect(() => {
    if (editingBranch) {
      const allAmenities = editingBranch.amenities || [];
      const selected = allAmenities.filter((a) =>
        AMENITIES_LIST.some((amen) => amen.id === a),
      );
      const other = allAmenities.filter(
        (a) => !AMENITIES_LIST.some((amen) => amen.id === a),
      );

      setForm({
        brandId: editingBranch.brandId,
        name: editingBranch.name,
        address: editingBranch.address || "",
        lat: editingBranch.lat,
        lng: editingBranch.lng,
        operatingHoursFrom: editingBranch.operatingHoursFrom,
        operatingHoursTo: editingBranch.operatingHoursTo,
        amenities: selected,
        description: editingBranch.description,
        ctaLink: editingBranch.ctaLink,
        images: editingBranch.images,
        isActive: editingBranch.isActive ?? true,
        categories: editingBranch.categories || [],
      });
      setOtherAmenities(other);
      setExistingImages(editingBranch.images);
      setSelectedBrandId(editingBranch.brandId);
      setSelectedCategories(editingBranch.categories || []);
      setNewImageFiles([]);

      if (editingBranch.openingHours) {
        const days = [
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
          "sunday",
        ];
        const allSame = days.every(
          (day) =>
            editingBranch.openingHours?.[day]?.start_time ===
              editingBranch.operatingHoursFrom &&
            editingBranch.openingHours?.[day]?.end_time ===
              editingBranch.operatingHoursTo,
        );
        if (allSame) {
          setOpeningHoursMode("same");
          setForm((prev) => ({
            ...prev,
            operatingHoursFrom:
              editingBranch.openingHours?.["monday"]?.start_time || "06:00",
            operatingHoursTo:
              editingBranch.openingHours?.["monday"]?.end_time || "22:00",
          }));
        } else {
          setOpeningHoursMode("perDay");
          const perDay: Record<
            string,
            { start: string; end: string; isClosed: boolean }
          > = {} as any;
          days.forEach((day) => {
            const start =
              editingBranch.openingHours?.[day]?.start_time || "06:00";
            const end = editingBranch.openingHours?.[day]?.end_time || "22:00";
            const isClosed = start === "00:00" && end === "00:00";
            perDay[day] = {
              start: isClosed ? "06:00" : start,
              end: isClosed ? "22:00" : end,
              isClosed,
            };
          });
          setOpeningHoursPerDay(perDay);
        }
      } else {
        setOpeningHoursMode("same");
      }
    } else {
      setForm(emptyForm(brandId));
      setExistingImages([]);
      setNewImageFiles([]);
      setOtherAmenities([]);
      setOtherAmenityInput("");
      setOpeningHoursMode("same");
      setOpeningHoursPerDay({
        monday: { start: "06:00", end: "22:00", isClosed: false },
        tuesday: { start: "06:00", end: "22:00", isClosed: false },
        wednesday: { start: "06:00", end: "22:00", isClosed: false },
        thursday: { start: "06:00", end: "22:00", isClosed: false },
        friday: { start: "06:00", end: "22:00", isClosed: false },
        saturday: { start: "06:00", end: "22:00", isClosed: false },
        sunday: { start: "06:00", end: "22:00", isClosed: false },
      });
      setSelectedBrandId(brandId || "");
      setSelectedCategories([]);
    }
    setErrors({});
    setTouched({});
  }, [editingBranch, brandId, show]);

  useEffect(() => {
    if (!editingBranch) {
      setForm((prev) => ({ ...prev, brandId: selectedBrandId }));
    }
  }, [selectedBrandId, editingBranch]);

  useEffect(() => {
    if (brandOptions && brandOptions.length > 0) {
      const isValid = brandOptions.some((b) => b.id === selectedBrandId);
      if (!isValid) {
        setSelectedBrandId("");
      }
    } else {
      setSelectedBrandId("");
    }
  }, [brandOptions, selectedBrandId]);

  const validateField = (field: keyof typeof form, value: any): string => {
    switch (field) {
      case "name":
        if (!value?.trim()) return "Tên chi nhánh là bắt buộc";
        if (value.trim().length < 2) return "Tên phải có ít nhất 2 ký tự";
        return "";
      case "address":
        if (!value?.trim()) return "Địa chỉ là bắt buộc";
        if (value.trim().length > 500) return "Địa chỉ tối đa 500 ký tự";
        return "";
      default:
        return "";
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const fields: (keyof typeof form)[] = ["name", "address"];
    for (const field of fields) {
      const error = validateField(field, form[field]);
      if (error) newErrors[field] = error;
    }
    const totalImages = existingImages.length + newImageFiles.length;
    if (totalImages < 3) {
      newErrors.images = "Vui lòng tải lên ít nhất 3 ảnh";
    }
    if (form.amenities.length === 0 && otherAmenities.length === 0) {
      newErrors.amenities = "Vui lòng chọn ít nhất một tiện ích";
    }
    if (form.lat != null && form.lng == null) {
      newErrors.lng = "Kinh độ là bắt buộc khi có vĩ độ";
    }
    if (form.lng != null && form.lat == null) {
      newErrors.lat = "Vĩ độ là bắt buộc khi có kinh độ";
    }
    if (form.lat != null && (form.lat < -90 || form.lat > 90)) {
      newErrors.lat = "Vĩ độ phải nằm trong khoảng -90 đến 90";
    }
    if (form.lng != null && (form.lng < -180 || form.lng > 180)) {
      newErrors.lng = "Kinh độ phải nằm trong khoảng -180 đến 180";
    }
    if (!editingBranch && !selectedBrandId) {
      newErrors.brandId = "Vui lòng chọn thương hiệu";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (field: keyof typeof form) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, form[field]);
    setErrors((prev) => {
      if (error) return { ...prev, [field]: error };
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  };

  const uploadImage = async (
    file: File,
  ): Promise<{ success: boolean; data?: BranchImage; error?: string }> => {
    try {
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        return {
          success: false,
          error: `Ảnh ${file.name} vượt quá ${MAX_IMAGE_SIZE_MB}MB`,
        };
      }
      const result = await gymPartnerService.uploadMedia(file, authArgs);
      return {
        success: true,
        data: {
          url: result.url,
          key: result.key,
          category: CategoryImage.GYM,
        },
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || `Tải ảnh thất bại: ${file.name}`,
      };
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canManage) {
      message.error("Bạn không có quyền quản lý chi nhánh.");
      return;
    }
    const allTouched: Record<string, boolean> = {};
    Object.keys(form).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);
    if (!validateForm()) {
      message.error("Vui lòng hoàn thành các trường bắt buộc");
      return;
    }

    setSaving(true);
    setUploading(true);
    try {
      const uploadedNewImages: BranchImage[] = [];
      const imageErrors: string[] = [];
      for (const item of newImageFiles) {
        const result = await uploadImage(item.file);
        if (result.success && result.data) {
          if (item.category) {
            result.data.category = item.category;
          }
          uploadedNewImages.push(result.data);
        } else {
          imageErrors.push(result.error || `Tải ${item.file.name} thất bại`);
        }
      }
      if (imageErrors.length > 0) {
        message.warning(`Có ${imageErrors.length} ảnh tải lên thất bại.`);
      }

      const finalImages = [...existingImages, ...uploadedNewImages];

      const finalAmenities = [...form.amenities, ...otherAmenities];
      const dataToSend = {
        ...form,
        amenities: finalAmenities,
        categories: selectedCategories,
        images: finalImages,
        isActive: form.isActive,
      };

      let openingHoursOverride:
        | Record<string, { start_time: string; end_time: string }>
        | undefined;
      if (openingHoursMode === "perDay") {
        const days = [
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
          "sunday",
        ];
        // Luôn tạo đủ 7 ngày, kể cả khi không nghỉ
        openingHoursOverride = days.reduce((acc, day) => {
          const data = openingHoursPerDay[day];
          if (data?.isClosed) {
            acc[day] = { start_time: "00:00", end_time: "00:00" };
          } else {
            acc[day] = {
              start_time: data?.start || "06:00",
              end_time: data?.end || "22:00",
            };
          }
          return acc;
        }, {} as any);
      }

      await onSave(dataToSend, openingHoursOverride);
      onClose();
    } catch (err: any) {
      message.error(err.message || "Lưu chi nhánh thất bại");
    } finally {
      setSaving(false);
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleChange = (field: keyof typeof form, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  };

  const toggleAmenity = (amenityId: string) => {
    setForm((prev) => ({
      ...prev,
      amenities:
        prev.amenities.indexOf(amenityId) !== -1
          ? prev.amenities.filter((id) => id !== amenityId)
          : [...prev.amenities, amenityId],
    }));
    setErrors((prev) => {
      const { amenities, ...rest } = prev;
      return rest;
    });
  };

  const handleImageFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newItems = Array.from(files).map((file) => ({
      file,
      category: CategoryImage.GYM,
      preview: URL.createObjectURL(file),
    }));
    setNewImageFiles((prev) => [...prev, ...newItems]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setErrors((prev) => {
      const { images, ...rest } = prev;
      return rest;
    });
  };

  const removeImage = (index: number, isExisting: boolean) => {
    if (isExisting) {
      const updated = [...existingImages];
      updated.splice(index, 1);
      setExistingImages(updated);
    } else {
      const updated = [...newImageFiles];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      setNewImageFiles(updated);
    }
    setErrors((prev) => {
      const { images, ...rest } = prev;
      return rest;
    });
  };

  const toggleCategory = (value: string) => {
    setSelectedCategories((prev) =>
      prev.indexOf(value) !== -1
        ? prev.filter((v) => v !== value)
        : [...prev, value],
    );
  };

  const handleApplyGoogleMapsCoords = (lat: number, long: number) => {
    setForm((prev) => ({ ...prev, lat, lng: long }));
    setErrors((prev) => {
      const { lat: _lat, lng: _lng, ...rest } = prev;
      return rest;
    });
  };

  const getFieldError = (
    field: keyof typeof form | "brandId" | "lat" | "lng",
  ): string => {
    return touched[field] ? errors[field] || "" : "";
  };

  const isFieldInvalid = (
    field: keyof typeof form | "brandId" | "lat" | "lng",
  ): boolean => {
    return !!touched[field] && !!errors[field];
  };

  const inputClassName = (
    field: keyof typeof form | "brandId" | "lat" | "lng",
  ): string => {
    return `form-control ${isFieldInvalid(field) ? "is-invalid" : ""}`;
  };

  const formatCoord = (value: number | null) =>
    value == null ? "" : String(value);

  const parseCoordInput = (raw: string): number | null => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  };

  const totalImages = existingImages.length + newImageFiles.length;

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      className="branch-form-modal"
    >
      <Modal.Header closeButton className="border-0 pb-0 pt-3 px-4">
        <Modal.Title className="fw-bold fs-4">
          {editingBranch ? "Chỉnh sửa chi nhánh" : "Thêm chi nhánh mới"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4 pb-4">
        <form onSubmit={handleSubmit} noValidate>
          {brandOptions && !editingBranch && (
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                Thương hiệu <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                className={inputClassName("brandId")}
                value={selectedBrandId}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedBrandId(val);
                  if (onBrandChange) onBrandChange(val);
                }}
                onBlur={() =>
                  setTouched((prev) => ({ ...prev, brandId: true }))
                }
                required
              >
                <option value="">-- Chọn thương hiệu --</option>
                {brandOptions.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Form.Select>
              {getFieldError("brandId") && (
                <Form.Text className="text-danger d-block">
                  {getFieldError("brandId")}
                </Form.Text>
              )}
            </Form.Group>
          )}

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Tên chi nhánh <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  className={inputClassName("name")}
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  onBlur={() => handleBlur("name")}
                  placeholder="VD: Cơ sở Quận 1"
                  isInvalid={!!getFieldError("name")}
                />
                <Form.Control.Feedback type="invalid">
                  {getFieldError("name")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Địa chỉ <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  className={inputClassName("address")}
                  value={form.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  onBlur={() => handleBlur("address")}
                  placeholder="Nhập địa chỉ chi tiết"
                  isInvalid={!!getFieldError("address")}
                  maxLength={500}
                />
                <Form.Control.Feedback type="invalid">
                  {getFieldError("address")}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  {form.address.length}/500 ký tự
                </Form.Text>
              </Form.Group>
            </Col>

            <Col xs={12}>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-1">
                <Form.Label className="fw-semibold mb-0">
                  Tọa độ{" "}
                  <span className="text-muted fw-normal">(tùy chọn)</span>
                </Form.Label>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowGoogleMapsModal(true)}
                  className="d-flex align-items-center gap-1"
                >
                  <i className="bi bi-geo-alt" />
                  Nhập từ Google Maps
                </Button>
              </div>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    className={inputClassName("lat")}
                    value={formatCoord(form.lat)}
                    onChange={(e) =>
                      handleChange("lat", parseCoordInput(e.target.value))
                    }
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, lat: true }))
                    }
                    placeholder="Vĩ độ (VD: 10.8063884)"
                    isInvalid={!!getFieldError("lat")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getFieldError("lat")}
                  </Form.Control.Feedback>
                </Col>
                <Col md={6}>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    className={inputClassName("lng")}
                    value={formatCoord(form.lng)}
                    onChange={(e) =>
                      handleChange("lng", parseCoordInput(e.target.value))
                    }
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, lng: true }))
                    }
                    placeholder="Kinh độ (VD: 106.6342355)"
                    isInvalid={!!getFieldError("lng")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getFieldError("lng")}
                  </Form.Control.Feedback>
                </Col>
              </Row>
            </Col>

            <Col xs={12}>
              <Form.Label className="fw-semibold">Giờ mở cửa</Form.Label>
              <div className="mb-2">
                <div className="btn-group">
                  <Button
                    variant={
                      openingHoursMode === "same"
                        ? "primary"
                        : "outline-secondary"
                    }
                    onClick={() => setOpeningHoursMode("same")}
                  >
                    Giống nhau mọi ngày
                  </Button>
                  <Button
                    variant={
                      openingHoursMode === "perDay"
                        ? "primary"
                        : "outline-secondary"
                    }
                    onClick={() => setOpeningHoursMode("perDay")}
                  >
                    Tùy chỉnh theo ngày
                  </Button>
                </div>
              </div>

              {openingHoursMode === "same" ? (
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Label className="small">Mở từ</Form.Label>
                    <Form.Control
                      type="time"
                      value={form.operatingHoursFrom}
                      onChange={(e) =>
                        handleChange("operatingHoursFrom", e.target.value)
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small">Mở đến</Form.Label>
                    <Form.Control
                      type="time"
                      value={form.operatingHoursTo}
                      onChange={(e) =>
                        handleChange("operatingHoursTo", e.target.value)
                      }
                    />
                  </Col>
                </Row>
              ) : (
                <div
                  className="border rounded-3 p-2"
                  style={{ maxHeight: 320, overflowY: "auto" }}
                >
                  {[
                    "monday",
                    "tuesday",
                    "wednesday",
                    "thursday",
                    "friday",
                    "saturday",
                    "sunday",
                  ].map((day) => {
                    const dayData = openingHoursPerDay[day] || {
                      start: "06:00",
                      end: "22:00",
                      isClosed: false,
                    };
                    const dayLabel =
                      day === "monday"
                        ? "Thứ 2"
                        : day === "tuesday"
                          ? "Thứ 3"
                          : day === "wednesday"
                            ? "Thứ 4"
                            : day === "thursday"
                              ? "Thứ 5"
                              : day === "friday"
                                ? "Thứ 6"
                                : day === "saturday"
                                  ? "Thứ 7"
                                  : "Chủ nhật";

                    return (
                      <div
                        key={day}
                        className="d-flex align-items-center gap-2 mb-1"
                        style={{ padding: "2px 0" }}
                      >
                        <div
                          className="text-capitalize small fw-medium"
                          style={{ minWidth: "65px", flexShrink: 0 }}
                        >
                          {dayLabel}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Form.Control
                            type="time"
                            size="sm"
                            value={dayData.start}
                            onChange={(e) =>
                              setOpeningHoursPerDay((prev) => ({
                                ...prev,
                                [day]: {
                                  ...prev[day],
                                  start: e.target.value,
                                  isClosed: false,
                                },
                              }))
                            }
                            disabled={dayData.isClosed}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Form.Control
                            type="time"
                            size="sm"
                            value={dayData.end}
                            onChange={(e) =>
                              setOpeningHoursPerDay((prev) => ({
                                ...prev,
                                [day]: {
                                  ...prev[day],
                                  end: e.target.value,
                                  isClosed: false,
                                },
                              }))
                            }
                            disabled={dayData.isClosed}
                          />
                        </div>
                        <div
                          className="d-flex align-items-center gap-2"
                          style={{ flexShrink: 0, minWidth: "85px" }}
                        >
                          <Switch
                            size="small"
                            checked={dayData.isClosed}
                            onChange={(checked) => {
                              setOpeningHoursPerDay((prev) => ({
                                ...prev,
                                [day]: {
                                  start: checked ? "00:00" : "06:00",
                                  end: checked ? "00:00" : "22:00",
                                  isClosed: checked,
                                },
                              }));
                            }}
                          />
                          <span
                            className="small fw-medium"
                            style={{ fontSize: "0.85rem", color: "#495057" }}
                          >
                            Nghỉ
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Col>

            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">Liên kết CTA</Form.Label>
                <Form.Control
                  type="text"
                  className={inputClassName("ctaLink")}
                  value={form.ctaLink}
                  onChange={(e) => handleChange("ctaLink", e.target.value)}
                  onBlur={() => handleBlur("ctaLink")}
                  placeholder="Zalo, Facebook, URL..."
                  isInvalid={!!getFieldError("ctaLink")}
                />
                <Form.Control.Feedback type="invalid">
                  {getFieldError("ctaLink")}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  <i className="bi bi-link-45deg me-1" />
                  Zalo OA, Facebook Page hoặc trang web
                </Form.Text>
              </Form.Group>
            </Col>

            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">Mô tả</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Mô tả chi tiết về chi nhánh..."
                  maxLength={1500}
                  className="resize-none"
                />
                <Form.Text className="text-muted text-end d-block">
                  {form.description.length}/1500 ký tự
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Amenities - Dropdown multiselect với badge hiển thị bên dưới */}
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Tiện ích <span className="text-danger">*</span>
                </Form.Label>
                <Dropdown
                  show={amenitiesDropdownOpen}
                  onToggle={setAmenitiesDropdownOpen}
                  className="w-100"
                  autoClose="outside"
                >
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    className="w-100 text-start d-flex justify-content-between align-items-center"
                    id="amenities-dropdown"
                  >
                    <span>
                      {form.amenities.length === 0
                        ? "Chọn tiện ích..."
                        : `Đã chọn ${form.amenities.length} tiện ích`}
                    </span>
                    <i className="bi bi-chevron-down" />
                  </Dropdown.Toggle>

                  <Dropdown.Menu
                    className="w-100 p-2"
                    style={{ maxHeight: 200, overflowY: "auto" }}
                  >
                    {AMENITIES_LIST.map((amenity: Amenity) => (
                      <Dropdown.Item
                        key={amenity.id}
                        as="div"
                        className="d-flex align-items-center gap-2"
                        onClick={(e) => {
                          e.preventDefault();
                          toggleAmenity(amenity.id);
                        }}
                      >
                        <Form.Check
                          type="checkbox"
                          id={`amenity-dropdown-${amenity.id}`}
                          label={amenity.label}
                          checked={form.amenities.indexOf(amenity.id) !== -1}
                          onChange={() => {}}
                        />
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                {/* Hiển thị badge cho các tiện ích đã chọn */}
                {form.amenities.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mt-2">
                    {form.amenities.map((amenityId) => {
                      const label =
                        AMENITIES_LIST.find((a) => a.id === amenityId)?.label ||
                        amenityId;
                      return (
                        <Badge
                          key={amenityId}
                          bg="secondary"
                          className="d-flex align-items-center gap-1 px-2 py-1"
                          style={{ fontWeight: 400 }}
                        >
                          {label}
                          <i
                            className="bi bi-x-circle cursor-pointer"
                            style={{ fontSize: "0.8rem", cursor: "pointer" }}
                            onClick={() => toggleAmenity(amenityId)}
                          />
                        </Badge>
                      );
                    })}
                  </div>
                )}

                {errors.amenities && (
                  <Form.Text className="text-danger d-block">
                    {errors.amenities}
                  </Form.Text>
                )}
                <Form.Text className="text-muted">
                  Đã chọn: {form.amenities.length} / tối thiểu 1
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Tiện ích khác */}
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">Tiện ích khác</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Nhập tiện ích và nhấn Enter"
                    value={otherAmenityInput}
                    onChange={(e) => setOtherAmenityInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && otherAmenityInput.trim()) {
                        e.preventDefault();
                        setOtherAmenities((prev) => [
                          ...prev,
                          otherAmenityInput.trim(),
                        ]);
                        setOtherAmenityInput("");
                      }
                    }}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      if (otherAmenityInput.trim()) {
                        setOtherAmenities((prev) => [
                          ...prev,
                          otherAmenityInput.trim(),
                        ]);
                        setOtherAmenityInput("");
                      }
                    }}
                  >
                    Thêm
                  </Button>
                </div>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {otherAmenities.map((item, idx) => (
                    <Badge
                      key={idx}
                      bg="light"
                      className="text-dark border d-flex align-items-center gap-1 px-2 py-1"
                      style={{ fontWeight: 400 }}
                    >
                      {item}
                      <i
                        className="bi bi-x-circle cursor-pointer"
                        style={{ fontSize: "0.8rem", cursor: "pointer" }}
                        onClick={() =>
                          setOtherAmenities((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                      />
                    </Badge>
                  ))}
                </div>
                <Form.Text className="text-muted">
                  Thêm tiện ích tùy chỉnh không có trong danh sách trên
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Categories - Dropdown multiselect với autoClose="outside" */}
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">Danh mục</Form.Label>
                <Dropdown
                  show={categoryDropdownOpen}
                  onToggle={setCategoryDropdownOpen}
                  className="w-100"
                  autoClose="outside"
                >
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    className="w-100 text-start d-flex justify-content-between align-items-center"
                    id="category-dropdown"
                  >
                    <span>
                      {selectedCategories.length === 0
                        ? "Chọn danh mục..."
                        : `Đã chọn ${selectedCategories.length} danh mục`}
                    </span>
                    <i className="bi bi-chevron-down" />
                  </Dropdown.Toggle>

                  <Dropdown.Menu
                    className="w-100 p-2"
                    style={{ maxHeight: 200, overflowY: "auto" }}
                  >
                    {CATEGORY_OPTIONS.map((cat) => (
                      <Dropdown.Item
                        key={cat.value}
                        as="div"
                        className="d-flex align-items-center gap-2"
                        onClick={(e) => {
                          e.preventDefault();
                          toggleCategory(cat.value);
                        }}
                      >
                        <Form.Check
                          type="checkbox"
                          id={`category-${cat.value}`}
                          label={cat.label}
                          checked={selectedCategories.indexOf(cat.value) !== -1}
                          onChange={() => {}}
                        />
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                {selectedCategories.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mt-2">
                    {selectedCategories.map((cat) => {
                      const label =
                        CATEGORY_OPTIONS.find((c) => c.value === cat)?.label ||
                        cat;
                      return (
                        <Badge
                          key={cat}
                          bg="primary"
                          className="d-flex align-items-center gap-1 px-2 py-1"
                          style={{ fontWeight: 400 }}
                        >
                          {label}
                          <i
                            className="bi bi-x-circle cursor-pointer"
                            style={{ fontSize: "0.8rem", cursor: "pointer" }}
                            onClick={() => toggleCategory(cat)}
                          />
                        </Badge>
                      );
                    })}
                  </div>
                )}
                <Form.Text className="text-muted">
                  Chọn một hoặc nhiều danh mục cho chi nhánh
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Thư viện ảnh */}
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Thư viện ảnh<span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  ref={fileInputRef}
                  type="file"
                  className={isFieldInvalid("images") ? "is-invalid" : ""}
                  accept="image/*"
                  multiple
                  onChange={handleImageFileSelect}
                  disabled={saving || uploading}
                />
                {getFieldError("images") && (
                  <Form.Text className="text-danger d-block">
                    {getFieldError("images")}
                  </Form.Text>
                )}
                <div className="d-flex flex-wrap gap-3 mt-2">
                  {existingImages.map((img, idx) => (
                    <div
                      key={`existing-${idx}`}
                      className="position-relative border rounded p-1"
                      style={{ width: 130 }}
                    >
                      <img
                        src={img.url}
                        alt={`branch-${idx}`}
                        className="rounded w-100"
                        style={{ height: 80, objectFit: "cover" }}
                      />
                      <Form.Select
                        size="sm"
                        className="mt-1"
                        value={img.category || CategoryImage.GYM}
                        onChange={(e) => {
                          const updated = [...existingImages];
                          updated[idx] = {
                            ...updated[idx],
                            category: e.target.value,
                          };
                          setExistingImages(updated);
                        }}
                        disabled={saving || uploading}
                      >
                        {Object.keys(CategoryImage).map((key) => {
                          const cat =
                            CategoryImage[key as keyof typeof CategoryImage];
                          return (
                            <option key={cat} value={cat}>
                              {cat.replace(/_/g, " ")}
                            </option>
                          );
                        })}
                      </Form.Select>
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 rounded-circle p-0 d-flex align-items-center justify-content-center"
                        style={{ width: 20, height: 20, fontSize: 12 }}
                        onClick={() => removeImage(idx, true)}
                        disabled={saving || uploading}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  {newImageFiles.map((item, idx) => (
                    <div
                      key={`new-${idx}`}
                      className="position-relative border rounded p-1"
                      style={{ width: 130 }}
                    >
                      <img
                        src={item.preview}
                        alt={`new-${idx}`}
                        className="rounded w-100"
                        style={{ height: 80, objectFit: "cover" }}
                      />
                      <Form.Select
                        size="sm"
                        className="mt-1"
                        value={item.category || CategoryImage.GYM}
                        onChange={(e) => {
                          const updated = [...newImageFiles];
                          updated[idx] = {
                            ...updated[idx],
                            category: e.target.value,
                          };
                          setNewImageFiles(updated);
                        }}
                        disabled={saving || uploading}
                      >
                        {Object.keys(CategoryImage).map((key) => {
                          const cat =
                            CategoryImage[key as keyof typeof CategoryImage];
                          return (
                            <option key={cat} value={cat}>
                              {cat.replace(/_/g, " ")}
                            </option>
                          );
                        })}
                      </Form.Select>
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 rounded-circle p-0 d-flex align-items-center justify-content-center"
                        style={{ width: 20, height: 20, fontSize: 12 }}
                        onClick={() => removeImage(idx, false)}
                        disabled={saving || uploading}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  {totalImages === 0 && (
                    <div className="text-muted small">Chưa có ảnh nào</div>
                  )}
                </div>
                <Form.Text className="text-muted">
                  {totalImages}/3 tối thiểu. PNG, JPG, JPEG. Chọn danh mục cho
                  từng ảnh.
                </Form.Text>
              </Form.Group>
            </Col>

            <Col xs={12} className="d-flex align-items-center gap-3">
              <Form.Label className="fw-semibold mb-0">Trạng thái</Form.Label>
              <Switch
                checked={form.isActive}
                onChange={(checked) => handleChange("isActive", checked)}
                checkedChildren="Hoạt động"
                unCheckedChildren="Không hoạt động"
              />
            </Col>
          </Row>

          <div className="text-end mt-4 pt-3 border-top">
            <Button
              variant="light"
              className="me-2 px-4"
              onClick={onClose}
              disabled={saving || uploading}
            >
              Hủy
            </Button>
            <Button
              variant="primary"
              type="submit"
              className="px-4"
              disabled={saving || uploading}
            >
              {saving || uploading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  {uploading ? "Đang tải lên..." : "Đang lưu..."}
                </>
              ) : (
                <>
                  <i className="bi bi-check2 me-2" />
                  {editingBranch ? "Cập nhật chi nhánh" : "Tạo chi nhánh"}
                </>
              )}
            </Button>
          </div>
        </form>
      </Modal.Body>

      <GoogleMapsLinkModal
        show={showGoogleMapsModal}
        onClose={() => setShowGoogleMapsModal(false)}
        onApply={handleApplyGoogleMapsCoords}
      />

      <style>
        {`
          .branch-form-modal .modal-content {
            border-radius: 1rem;
            border: none;
            box-shadow: 0 20px 60px rgba(0,0,0,0.12);
          }
          .branch-form-modal .form-control:focus,
          .branch-form-modal .form-select:focus {
            border-color: #86b7fe;
            box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.15);
          }
          .branch-form-modal .btn-group .btn {
            border-radius: 0.375rem;
          }
          .branch-form-modal .btn-group .btn:first-child {
            border-radius: 0.375rem 0 0 0.375rem;
          }
          .branch-form-modal .btn-group .btn:last-child {
            border-radius: 0 0.375rem 0.375rem 0;
          }
          .branch-form-modal .dropdown-toggle::after {
            margin-left: auto;
          }
          .resize-none {
            resize: none;
          }
          .cursor-pointer {
            cursor: pointer;
          }
        `}
      </style>
    </Modal>
  );
};
